import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { 
  AcademicCapIcon, 
  CalendarDaysIcon, 
  AcademicCapIcon as PerformanceIcon,
  ViewColumnsIcon,
  UserGroupIcon,
  ClockIcon,
  TrophyIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  StarIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import api from '../services/api';

const TeacherDashboardPage = () => {
  const { logout, user } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState({
    totalStudents: 0,
    totalCourses: 0,
    activeQuizzes: 0,
    averageScore: 0,
    upcomingDeadlines: [],
    courseStats: [],
    studentInsights: {
      topPerformers: 0,
      needAttention: 0,
      activeLearners: 0
    },
    isLoading: true
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setDashboardData(prev => ({ ...prev, isLoading: true }));

        console.log('🔄 Fetching dashboard data...');
        const apiCalls = [
          { name: 'courses', call: () => api.get('/courses') },
          { name: 'quizzes', call: () => api.get('/quizzes') },
          { name: 'submissions', call: () => api.get('/submissions') },
          { name: 'users', call: () => api.get('/users') }
        ];

        console.log('🔄 Fetching dashboard data...');
        
        const results = await Promise.allSettled(
          apiCalls.map(({ call }) => call())
        );

        // Log results for debugging
        results.forEach((result, index) => {
          const apiName = apiCalls[index].name;
          if (result.status === 'fulfilled') {
            console.log(`✅ ${apiName}:`, result.value.data);
          } else {
            console.error(`❌ ${apiName} failed:`, result.reason);
          }
        });

        // Process results with fallbacks and better error handling
        const [coursesResult, quizzesResult, submissionsResult, usersResult] = results;
        
        // Handle courses - API returns { data: [...], pagination: {...} }
        let courses = [];
        if (coursesResult.status === 'fulfilled') {
          const responseData = coursesResult.value.data;
          courses = responseData?.data || [];
        }

        // Handle quizzes - API returns { data: [...], meta: {...} }
        let quizzes = [];
        if (quizzesResult.status === 'fulfilled') {
          const responseData = quizzesResult.value.data;
          quizzes = responseData?.data || [];
        }

        // Handle submissions - API returns array directly
        let submissions = [];
        if (submissionsResult.status === 'fulfilled') {
          const data = submissionsResult.value.data;
          submissions = Array.isArray(data) ? data : [];
        }

        // Handle users - API returns array directly  
        let users = [];
        if (usersResult.status === 'fulfilled') {
          const data = usersResult.value.data;
          users = Array.isArray(data) ? data : [];
        }

        // Fetch enrollments for each course
        let allEnrollments = [];
        if (Array.isArray(courses) && courses.length > 0) {
          const enrollmentPromises = courses.map(course => 
            api.get(`/enrollments/course/${course.id}`)
              .then(res => ({ courseId: course.id, enrollments: res.data }))
              .catch(err => {
                console.error(`Failed to fetch enrollments for course ${course.id}:`, err);
                return { courseId: course.id, enrollments: [] };
              })
          );
          
          const enrollmentResults = await Promise.all(enrollmentPromises);
          allEnrollments = enrollmentResults.flatMap(result => 
            Array.isArray(result.enrollments) ? result.enrollments : []
          );
        }

        console.log('📊 Processed data:', { courses: courses.length, quizzes: quizzes.length, submissions: submissions.length, users: users.length, enrollments: allEnrollments.length });

        // If all APIs return empty, show a message in development
        const hasNoData = courses.length === 0 && quizzes.length === 0 && submissions.length === 0 && users.length === 0 && allEnrollments.length === 0;
        if (hasNoData && process.env.NODE_ENV === 'development') {
          console.log('⚠️ No data returned from any API endpoints. This could mean:');
          console.log('1. Database is empty');
          console.log('2. Authentication issues');
          console.log('3. API endpoints have changed');
          console.log('4. Backend server is not running');
        }

        // Calculate metrics with proper data structures
        const totalCourses = Array.isArray(courses) ? courses.length : 0;
        
        // Calculate total enrolled students across all teacher's courses
        const totalEnrolledStudents = Array.isArray(allEnrollments)
          ? new Set(allEnrollments.map(e => e.userId)).size
          : 0;
        
        // Use only the enrolled students count, not a fallback
        const totalStudents = totalEnrolledStudents;
        
        const activeQuizzes = Array.isArray(quizzes) 
          ? quizzes.filter(quiz => {
              // Consider quiz active if no closeTime or closeTime is in the future
              if (!quiz.closeTime) return true;
              return new Date(quiz.closeTime) > new Date();
            }).length 
          : 0;

        // Calculate average score from submissions
        const validSubmissions = Array.isArray(submissions) 
          ? submissions.filter(s => s.score !== null && s.score !== undefined && !isNaN(s.score))
          : [];
        
        const averageScore = validSubmissions.length > 0 
          ? (validSubmissions.reduce((sum, sub) => sum + (parseFloat(sub.score) || 0), 0) / validSubmissions.length).toFixed(1)
          : 0;

        // Generate upcoming deadlines from quizzes with proper date handling
        const upcomingDeadlines = Array.isArray(quizzes)
          ? quizzes
              .filter(quiz => quiz.closeTime && new Date(quiz.closeTime) > new Date())
              .sort((a, b) => new Date(a.closeTime) - new Date(b.closeTime))
              .slice(0, 3)
              .map(quiz => ({
                id: quiz.id,
                title: quiz.title || 'Untitled Quiz',
                course: quiz.course?.title || 'Unknown Course',
                dueDate: quiz.closeTime,
                type: 'quiz'
              }))
          : [];

        // Ensure upcomingDeadlines is always an array
        const safeUpcomingDeadlines = Array.isArray(upcomingDeadlines) ? upcomingDeadlines : [];

        // Generate course statistics with enrollment and submission data
        const courseStats = Array.isArray(courses)
          ? courses.slice(0, 3).map(course => {
              // Count students enrolled in this course from enrollments table
              const courseEnrollments = allEnrollments.filter(e => e.courseId === course.id);
              const enrolledStudentCount = courseEnrollments.length;
              
              // Find submissions for this course
              const courseSubmissions = submissions.filter(sub => 
                sub.quiz?.courseId === course.id || 
                sub.quiz?.course?.id === course.id
              );
              
              // Calculate average score for this course
              const validCourseSubmissions = courseSubmissions.filter(s => 
                s.score !== null && s.score !== undefined && !isNaN(s.score)
              );
              
              const courseAverage = validCourseSubmissions.length > 0
                ? validCourseSubmissions.reduce((sum, sub) => sum + (parseFloat(sub.score) || 0), 0) / validCourseSubmissions.length
                : 0;
              
              return {
                name: course.title || course.name || 'Untitled Course',
                students: enrolledStudentCount, // Use actual enrollment count
                averageScore: courseAverage.toFixed(1),
                trend: courseAverage > 75 ? '+3.2%' : courseAverage > 60 ? '+1.5%' : '-0.8%'
              };
            })
          : [];

        // Calculate student insights from real data
        const validScores = validSubmissions.map(s => parseFloat(s.score) || 0);
        const topPerformers = validScores.filter(score => score >= 90).length;
        const needAttention = validScores.filter(score => score < 50).length;
        
        // Calculate active learners (students who submitted in the last 7 days)
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        
        const recentSubmissions = Array.isArray(submissions)
          ? submissions.filter(sub => {
              const submissionDate = new Date(sub.createdAt || sub.submittedAt);
              return submissionDate >= oneWeekAgo;
            })
          : [];
        
        const activeLearners = new Set(
          recentSubmissions.map(s => s.userId || s.user?.id)
        ).size;

        const finalData = {
          totalStudents,
          totalCourses,
          activeQuizzes,
          averageScore,
          upcomingDeadlines: safeUpcomingDeadlines,
          courseStats,
          studentInsights: {
            topPerformers,
            needAttention,
            activeLearners
          },
          isLoading: false
        };

        console.log('📈 Final dashboard data:', finalData);
        setDashboardData(finalData);

      } catch (error) {
        console.error('💥 Error fetching dashboard data:', error);
        
        // Set fallback data in case of API errors
        setDashboardData({
          totalStudents: 0,
          totalCourses: 0,
          activeQuizzes: 0,
          averageScore: 0,
          upcomingDeadlines: [],
          courseStats: [],
          studentInsights: {
            topPerformers: 0,
            needAttention: 0,
            activeLearners: 0
          },
          isLoading: false
        });
      }
    };

    fetchDashboardData();
  }, []);

  if (dashboardData.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Dashboard</h2>
          <p className="text-gray-600">Preparing your teaching overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                <span className="text-4xl">👨‍🏫</span>
                <span>Welcome back, {user?.name || 'Teacher'}!</span>
              </h1>
              <p className="text-gray-600 mt-2">Here&apos;s your teaching overview for today</p>
            </div>
            <button
              onClick={logout}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg transition-colors flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Debug Information - Remove this in production */}
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Enrolled Students</p>
                <p className="text-3xl font-bold text-blue-600">{dashboardData.totalStudents || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Students in your courses</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <UserGroupIcon className="w-8 h-8 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Active Courses</p>
                <p className="text-3xl font-bold text-green-600">{dashboardData.totalCourses || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Courses you&apos;re teaching</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <AcademicCapIcon className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Active Quizzes</p>
                <p className="text-3xl font-bold text-purple-600">{dashboardData.activeQuizzes || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Quizzes currently available</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <DocumentTextIcon className="w-8 h-8 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Average Score</p>
                <p className="text-3xl font-bold text-yellow-600">{dashboardData.averageScore || 0}%</p>
                <p className="text-sm text-gray-500 mt-1">Across all submissions</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <TrophyIcon className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Upcoming Deadlines */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <span className="text-2xl">⏰</span>
              <span>Upcoming Deadlines</span>
            </h2>
            <div className="space-y-4">
              {(dashboardData.upcomingDeadlines && dashboardData.upcomingDeadlines.length > 0) ? (
                dashboardData.upcomingDeadlines.map((deadline) => (
                  <div key={deadline.id} className="border-l-4 border-orange-400 bg-orange-50 p-4 rounded-r-lg hover:bg-orange-100 transition-colors duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{deadline.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        deadline.type === 'quiz' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {deadline.type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{deadline.course}</p>
                    <p className="text-xs text-orange-600 font-medium flex items-center space-x-1">
                      <ClockIcon className="w-3 h-3" />
                      <span>Due: {new Date(deadline.dueDate).toLocaleDateString()}</span>
                    </p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📅</div>
                  <p className="text-gray-500">No upcoming deadlines</p>
                  <p className="text-sm text-gray-400">Create some quizzes to see deadlines here</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Course Performance Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <span className="text-2xl">📊</span>
              <span>Course Performance</span>
            </h2>
            <div className="space-y-4">
              {dashboardData.courseStats.length > 0 ? (
                dashboardData.courseStats.map((course, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{course.name}</h3>
                      <span className={`text-sm font-medium ${
                        parseFloat(course.trend?.replace('%', '') || 0) > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {course.trend || 'No data'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>{course.students || 0} students</span>
                      <span className="font-medium text-blue-600">{course.averageScore || 0}% avg</span>
                    </div>
                    <div className="mt-2 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(parseFloat(course.averageScore) || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📊</div>
                  <p className="text-gray-500">No course data available</p>
                  <p className="text-sm text-gray-400">Course performance will appear here once you have students and submissions</p>
                </div>
              )}
            </div>
          </div>

          {/* Main Navigation */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <span className="text-2xl">🎯</span>
              <span>Main Navigation</span>
            </h2>
            <div className="grid grid-cols-1 gap-4">
              <Link to="/teachers/dashboard/courses" className="group">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl hover:from-blue-100 hover:to-blue-200 transition-all duration-300 border border-blue-200 hover:border-blue-300">
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-500 p-3 rounded-xl">
                      <AcademicCapIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">My Courses</h3>
                      <p className="text-sm text-gray-600">Manage courses and students</p>
                    </div>
                  </div>
                </div>
              </Link>

              <Link to="/teachers/dashboard/schedule" className="group">
                <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl hover:from-green-100 hover:to-green-200 transition-all duration-300 border border-green-200 hover:border-green-300">
                  <div className="flex items-center space-x-4">
                    <div className="bg-green-500 p-3 rounded-xl">
                      <CalendarDaysIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Schedule</h3>
                      <p className="text-sm text-gray-600">View teaching schedule</p>
                    </div>
                  </div>
                </div>
              </Link>

              {user.role === 'admin' && (
                <Link to="/teachers/dashboard/tables" className="group">
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl hover:from-purple-100 hover:to-purple-200 transition-all duration-300 border border-purple-200 hover:border-purple-300">
                    <div className="flex items-center space-x-4">
                      <div className="bg-purple-500 p-3 rounded-xl">
                        <ViewColumnsIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Table Management</h3>
                        <p className="text-sm text-gray-600">Create custom grade tables</p>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              <Link to="/teachers/dashboard/performance" className="group">
                <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-xl hover:from-yellow-100 hover:to-yellow-200 transition-all duration-300 border border-yellow-200 hover:border-yellow-300">
                  <div className="flex items-center space-x-4">
                    <div className="bg-yellow-500 p-3 rounded-xl">
                      <PerformanceIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Performance Analytics</h3>
                      <p className="text-sm text-gray-600">Analyze student performance</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Student Insights */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
            <span className="text-2xl">🌟</span>
            <span>Student Insights</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="bg-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <StarIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-green-600 mb-2">Top Performers</h3>
              <p className="text-sm text-gray-600">
                {dashboardData.studentInsights.topPerformers} {dashboardData.studentInsights.topPerformers === 1 ? 'student' : 'students'} scored above 90%
              </p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200">
              <div className="bg-orange-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ExclamationTriangleIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-orange-600 mb-2">Need Attention</h3>
              <p className="text-sm text-gray-600">
                {dashboardData.studentInsights.needAttention} {dashboardData.studentInsights.needAttention === 1 ? 'student needs' : 'students need'} extra support
              </p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <PlayIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-blue-600 mb-2">Active Learners</h3>
              <p className="text-sm text-gray-600">
                {dashboardData.studentInsights.activeLearners} {dashboardData.studentInsights.activeLearners === 1 ? 'student' : 'students'} active this week
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboardPage;
